import React, { createContext, useContext, useState } from 'react';

const translations = {
  en: {
    // Common
    loading: "Loading...",
    save: "Save",
    cancel: "Cancel",
    delete: "Delete",
    edit: "Edit",
    close: "Close",
    confirm: "Confirm",
    yes: "Yes",
    no: "No",
    or: "or",
    and: "and",
    
    // Auth
    login: "Sign In",
    logout: "Sign Out",
    register: "Register",
    email: "Email",
    password: "Password",
    name: "Name",
    forgotPassword: "Forgot password?",
    noAccount: "Don't have an account?",
    hasAccount: "Already have an account?",
    createAccount: "Create one",
    signInHere: "Sign in here",
    welcomeBack: "Welcome back",
    signInContinue: "Sign in to continue creating amazing visual stories",
    joinCreators: "Join creators who use Script Visual to transform their YouTube scripts into professional storyboards.",
    getStarted: "Get Started",
    createYourAccount: "Create your account",
    
    // Dashboard
    dashboard: "Dashboard",
    myProjects: "My Projects",
    newProject: "New Project",
    searchProjects: "Search projects...",
    noProjects: "No projects yet",
    createFirst: "Create your first project to get started",
    createNewProject: "Create New Project",
    scenes: "scenes",
    scene: "scene",
    
    // Project Form
    projectTitle: "Project Title",
    projectTitlePlaceholder: "e.g., Genesis Chapter 1",
    description: "Description",
    descriptionPlaceholder: "Brief description of your project",
    style: "Style",
    pacing: "Pacing",
    createProject: "Create Project",
    creating: "Creating...",
    
    // Styles
    cinematic: "Cinematic",
    documentary: "Documentary",
    artistic: "Artistic",
    dramatic: "Dramatic",
    minimalist: "Minimalist",
    
    // Pacing
    balanced: "Balanced",
    fast: "Fast-paced",
    slow: "Slow/Contemplative",
    
    // Project Editor
    backToDashboard: "Back to Dashboard",
    generateAll: "Generate All",
    regenerate: "Regenerate",
    voiceover: "Voiceover",
    export: "Export",
    settings: "Settings",
    
    // Scenes Panel
    scenesPanel: "Scenes",
    noScenes: "No scenes yet",
    uploadScript: "Upload a script to generate scenes",
    uploadScriptBtn: "Upload Script",
    pasteScript: "Paste Script",
    duration: "Duration",
    seconds: "seconds",
    
    // Scene Details
    sceneDetails: "Scene Details",
    scriptExcerpt: "Script Excerpt",
    imagePrompt: "Image Prompt",
    videoPrompt: "Video Prompt",
    generate: "Generate",
    generating: "Generating...",
    regenerateImage: "Regenerate",
    searchStock: "Search Stock",
    
    // Aspect Ratios
    landscape: "Landscape",
    portrait: "Portrait",
    square: "Square",
    
    // Enhancement
    enhance: "Enhance",
    realistic: "Realistic",
    
    // Script Upload
    uploadYourScript: "Upload Your Script",
    supportedFormats: "Supported formats: TXT, DOCX, PDF",
    dropFileHere: "Drop file here or click to browse",
    processScript: "Process Script",
    processing: "Processing...",
    pasteYourScript: "Paste Your Script",
    pasteScriptPlaceholder: "Paste your script text here...",
    
    // Voiceover Modal
    voiceoverGenerator: "AI Voiceover Generator",
    selectVoice: "Select Voice",
    speed: "Speed",
    quality: "Quality",
    standard: "Standard",
    hd: "HD",
    sceneScripts: "Scene Scripts",
    customText: "Custom Text",
    generateAllVoiceovers: "Generate All",
    downloadAll: "Download All",
    customTextPlaceholder: "Paste your intro, outro, or any custom text here to generate voiceover...",
    characters: "characters",
    generateCustomVoiceover: "Generate Custom Voiceover",
    generatingVoiceover: "Generating...",
    
    // Voice descriptions
    voiceNova: "Energetic, upbeat",
    voiceCoral: "Warm, friendly", 
    voiceShimmer: "Bright, cheerful",
    voiceAsh: "Clear, articulate",
    voiceEcho: "Smooth, calm",
    voiceOnyx: "Deep, authoritative",
    voiceAlloy: "Neutral, balanced",
    voiceFable: "Expressive, storytelling",
    voiceSage: "Wise, measured",
    female: "Female",
    male: "Male",
    neutral: "Neutral",
    
    // Export Modal
    exportProject: "Export Project",
    downloadImages: "Download All Images (ZIP)",
    downloadImagesDesc: "All scene images in one ZIP file",
    downloadScript: "Download Original Script",
    downloadScriptDesc: "Your uploaded script as text file",
    downloadPdf: "Download PDF Storyboard",
    downloadPdfDesc: "Visual storyboard with thumbnails",
    downloadCsv: "Download CSV",
    downloadCsvDesc: "Scene data for spreadsheets",
    downloadJson: "Download JSON",
    downloadJsonDesc: "Complete project data",
    
    // Settings Modal
    projectSettings: "Project Settings",
    timePeriod: "Time Period / Era",
    timePeriodDesc: "Set historical context for accurate visuals",
    auto: "Auto (AI decides)",
    biblical: "Biblical / Ancient Middle East",
    ancient: "Ancient (Before 500 AD)",
    medieval: "Medieval (500-1500 AD)",
    renaissance: "Renaissance (1400-1600)",
    victorian: "Victorian (1837-1901)",
    modern: "Modern (Present day)",
    futuristic: "Futuristic",
    saveSettings: "Save Settings",
    
    // Stock Search
    stockSearch: "Stock Image Search",
    searchImages: "Search images...",
    search: "Search",
    useThisAsset: "Use This Asset",
    poweredByUnsplash: "Powered by Unsplash",
    
    // Coverage & Duplicates
    coverage: "Coverage",
    coverageScore: "Coverage Score",
    findDuplicates: "Find Duplicates",
    duplicateScenes: "Duplicate Scenes",
    noDuplicates: "No duplicate scenes found",
    
    // Version History
    versionHistory: "Version History",
    saveCurrentVersion: "Save Current",
    restoreVersion: "Restore",
    noVersions: "No saved versions yet",
    
    // Batch Regenerate
    batchRegenerate: "Batch Regenerate",
    selectScenes: "Select scenes to regenerate",
    selectAll: "Select All",
    deselectAll: "Deselect All",
    regenerateSelected: "Regenerate Selected",
    
    // Messages
    projectCreated: "Project created successfully",
    projectDeleted: "Project deleted",
    sceneUpdated: "Scene updated",
    imageGenerated: "Image generated successfully",
    voiceoverGenerated: "Voiceover generated successfully",
    settingsSaved: "Settings saved",
    errorOccurred: "An error occurred",
    pleaseWait: "Please wait...",
    
    // User Guide
    needHelp: "Need help?",
    userGuide: "User Guide",
  },
  
  es: {
    // Common
    loading: "Cargando...",
    save: "Guardar",
    cancel: "Cancelar",
    delete: "Eliminar",
    edit: "Editar",
    close: "Cerrar",
    confirm: "Confirmar",
    yes: "Sí",
    no: "No",
    or: "o",
    and: "y",
    
    // Auth
    login: "Iniciar Sesión",
    logout: "Cerrar Sesión",
    register: "Registrarse",
    email: "Correo Electrónico",
    password: "Contraseña",
    name: "Nombre",
    forgotPassword: "¿Olvidaste tu contraseña?",
    noAccount: "¿No tienes una cuenta?",
    hasAccount: "¿Ya tienes una cuenta?",
    createAccount: "Crear una",
    signInHere: "Inicia sesión aquí",
    welcomeBack: "Bienvenido de nuevo",
    signInContinue: "Inicia sesión para continuar creando historias visuales increíbles",
    joinCreators: "Únete a los creadores que usan Script Visual para transformar sus guiones de YouTube en storyboards profesionales.",
    getStarted: "Comenzar",
    createYourAccount: "Crea tu cuenta",
    
    // Dashboard
    dashboard: "Panel",
    myProjects: "Mis Proyectos",
    newProject: "Nuevo Proyecto",
    searchProjects: "Buscar proyectos...",
    noProjects: "Aún no hay proyectos",
    createFirst: "Crea tu primer proyecto para comenzar",
    createNewProject: "Crear Nuevo Proyecto",
    scenes: "escenas",
    scene: "escena",
    
    // Project Form
    projectTitle: "Título del Proyecto",
    projectTitlePlaceholder: "ej., Génesis Capítulo 1",
    description: "Descripción",
    descriptionPlaceholder: "Breve descripción de tu proyecto",
    style: "Estilo",
    pacing: "Ritmo",
    createProject: "Crear Proyecto",
    creating: "Creando...",
    
    // Styles
    cinematic: "Cinematográfico",
    documentary: "Documental",
    artistic: "Artístico",
    dramatic: "Dramático",
    minimalist: "Minimalista",
    
    // Pacing
    balanced: "Equilibrado",
    fast: "Rápido",
    slow: "Lento/Contemplativo",
    
    // Project Editor
    backToDashboard: "Volver al Panel",
    generateAll: "Generar Todo",
    regenerate: "Regenerar",
    voiceover: "Voz en Off",
    export: "Exportar",
    settings: "Configuración",
    
    // Scenes Panel
    scenesPanel: "Escenas",
    noScenes: "Aún no hay escenas",
    uploadScript: "Sube un guión para generar escenas",
    uploadScriptBtn: "Subir Guión",
    pasteScript: "Pegar Guión",
    duration: "Duración",
    seconds: "segundos",
    
    // Scene Details
    sceneDetails: "Detalles de Escena",
    scriptExcerpt: "Extracto del Guión",
    imagePrompt: "Prompt de Imagen",
    videoPrompt: "Prompt de Video",
    generate: "Generar",
    generating: "Generando...",
    regenerateImage: "Regenerar",
    searchStock: "Buscar Stock",
    
    // Aspect Ratios
    landscape: "Horizontal",
    portrait: "Vertical",
    square: "Cuadrado",
    
    // Enhancement
    enhance: "Mejorar",
    realistic: "Realista",
    
    // Script Upload
    uploadYourScript: "Sube Tu Guión",
    supportedFormats: "Formatos soportados: TXT, DOCX, PDF",
    dropFileHere: "Arrastra el archivo aquí o haz clic para buscar",
    processScript: "Procesar Guión",
    processing: "Procesando...",
    pasteYourScript: "Pega Tu Guión",
    pasteScriptPlaceholder: "Pega el texto de tu guión aquí...",
    
    // Voiceover Modal
    voiceoverGenerator: "Generador de Voz con IA",
    selectVoice: "Seleccionar Voz",
    speed: "Velocidad",
    quality: "Calidad",
    standard: "Estándar",
    hd: "HD",
    sceneScripts: "Guiones de Escenas",
    customText: "Texto Personalizado",
    generateAllVoiceovers: "Generar Todo",
    downloadAll: "Descargar Todo",
    customTextPlaceholder: "Pega tu intro, outro o cualquier texto personalizado aquí para generar voz en off...",
    characters: "caracteres",
    generateCustomVoiceover: "Generar Voz Personalizada",
    generatingVoiceover: "Generando...",
    
    // Voice descriptions
    voiceNova: "Energética, optimista",
    voiceCoral: "Cálida, amigable",
    voiceShimmer: "Brillante, alegre",
    voiceAsh: "Clara, articulada",
    voiceEcho: "Suave, calmada",
    voiceOnyx: "Profunda, autoritaria",
    voiceAlloy: "Neutral, equilibrada",
    voiceFable: "Expresiva, narrativa",
    voiceSage: "Sabia, mesurada",
    female: "Femenina",
    male: "Masculina",
    neutral: "Neutral",
    
    // Export Modal
    exportProject: "Exportar Proyecto",
    downloadImages: "Descargar Todas las Imágenes (ZIP)",
    downloadImagesDesc: "Todas las imágenes en un archivo ZIP",
    downloadScript: "Descargar Guión Original",
    downloadScriptDesc: "Tu guión subido como archivo de texto",
    downloadPdf: "Descargar Storyboard PDF",
    downloadPdfDesc: "Storyboard visual con miniaturas",
    downloadCsv: "Descargar CSV",
    downloadCsvDesc: "Datos de escenas para hojas de cálculo",
    downloadJson: "Descargar JSON",
    downloadJsonDesc: "Datos completos del proyecto",
    
    // Settings Modal
    projectSettings: "Configuración del Proyecto",
    timePeriod: "Período de Tiempo / Era",
    timePeriodDesc: "Configura el contexto histórico para visuales precisos",
    auto: "Auto (la IA decide)",
    biblical: "Bíblico / Antiguo Medio Oriente",
    ancient: "Antiguo (Antes del 500 DC)",
    medieval: "Medieval (500-1500 DC)",
    renaissance: "Renacimiento (1400-1600)",
    victorian: "Victoriano (1837-1901)",
    modern: "Moderno (Día presente)",
    futuristic: "Futurista",
    saveSettings: "Guardar Configuración",
    
    // Stock Search
    stockSearch: "Búsqueda de Imágenes de Stock",
    searchImages: "Buscar imágenes...",
    search: "Buscar",
    useThisAsset: "Usar Este Recurso",
    poweredByUnsplash: "Desarrollado por Unsplash",
    
    // Coverage & Duplicates
    coverage: "Cobertura",
    coverageScore: "Puntuación de Cobertura",
    findDuplicates: "Buscar Duplicados",
    duplicateScenes: "Escenas Duplicadas",
    noDuplicates: "No se encontraron escenas duplicadas",
    
    // Version History
    versionHistory: "Historial de Versiones",
    saveCurrentVersion: "Guardar Actual",
    restoreVersion: "Restaurar",
    noVersions: "Aún no hay versiones guardadas",
    
    // Batch Regenerate
    batchRegenerate: "Regeneración por Lotes",
    selectScenes: "Selecciona escenas para regenerar",
    selectAll: "Seleccionar Todo",
    deselectAll: "Deseleccionar Todo",
    regenerateSelected: "Regenerar Seleccionadas",
    
    // Messages
    projectCreated: "Proyecto creado exitosamente",
    projectDeleted: "Proyecto eliminado",
    sceneUpdated: "Escena actualizada",
    imageGenerated: "Imagen generada exitosamente",
    voiceoverGenerated: "Voz en off generada exitosamente",
    settingsSaved: "Configuración guardada",
    errorOccurred: "Ocurrió un error",
    pleaseWait: "Por favor espera...",
    
    // User Guide
    needHelp: "¿Necesitas ayuda?",
    userGuide: "Guía del Usuario",
  }
};

const LanguageContext = createContext();

export function LanguageProvider({ children }) {
  const [language, setLanguage] = useState('en');

  const t = (key) => {
    return translations[language][key] || translations['en'][key] || key;
  };

  const toggleLanguage = () => {
    setLanguage(prev => prev === 'en' ? 'es' : 'en');
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, toggleLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}

export default LanguageContext;
